import React, {
  useState, useEffect, useContext, useRef
} from "react";
import {
  Accordion, Card, Overlay, Tooltip
} from "react-bootstrap";
import {
  pdpService, serviceContent, yStars, clipStar, ratingText,
  scImg, sTitle, sPrice, sDescription, accordionCard, cardHeader,
  accordionIcon, accordionBody, accordionTitle, accordionSubtitle,
  sBtn, accordionIconOpen, serviceContentText, scFigure,
  freeReturns, frContent, shippingEta, tooltipInfo, serviceDescription
} from "./style.mod.scss";
import locale from "locales";
import RichText from "components/rich-text";
import Money from "components/money";
import GlobalContext from "services/global-context";
import { StarFill, ChevronDown } from "react-bootstrap-icons";
import getImagePath from "helpers/get-image-path";
import Truck from "assets/images/truck.svg";
import PDPCampaign from "components/pdp-campaign";
import CrossSellProducts from "components/cross-sell-pdp";
import { addCartItems, fetchCart } from "data/cart";
import { trackDropdownClick, trackAddedToCartKlaviyo } from "helpers/analytics";
import { getMergedReviewsForCluster } from "../../data/trusted-reviews";

const PdpService = ({ product, settings }) => {
  const { cartOpened, toggleCart, addToCart } = useContext(GlobalContext);
  const variant = product?.variants[0];
  const pdpTemplate = product?.content?.pdpTemplate;
  const miniIcons = variant?.productInfo?.miniIcons;

  const [totalPrice, setTotalPrice] = useState(parseFloat(variant.store.price));
  const [totalCompareAtPrice, setTotalCompareAtPrice] = useState(parseFloat(variant.store.compareAtPrice));
  const [crossSellVariants, setCrossSellVariants] = useState({});

  const [averageRating, setAverageRating] = useState(null);
  const [totalReviews, setTotalReviews] = useState(0);
  
  useEffect(() => {
    const fetchReviews = async () => {
      if (!variant) return;
      const serviceCluster = product.clusterReviews;
      const reviews = await getMergedReviewsForCluster(serviceCluster);
      const clusters = [variant.cluster].filter(Boolean);

      const filteredReviews = reviews.filter(review =>
        review.cluster && clusters.includes(review.cluster)
      );

      if (filteredReviews.length > 0) {
        const totalScore = filteredReviews.reduce((sum, review) => sum + (review.rating || 0), 0);
        setTotalReviews(filteredReviews.length);
        setAverageRating(totalScore / filteredReviews.length);
      }

    };

    fetchReviews();
  }, [variant]);

  const handleAddToCart = async () => {
    if (crossSellVariants) {
      const variantIds = Object.keys(crossSellVariants).filter(id => crossSellVariants[id]);
      variantIds.length && (await addCartItems(variantIds));
    }
    await addToCart(variant, []);
    const cart = await fetchCart();
    trackAddedToCartKlaviyo(cart, variant);

    if (!cartOpened) {
      toggleCart();
    }
  };

  if (!variant) return <></>;

  const {
    productInfo: {
      miniDescription,
      productTabs,
    },
    store: { price, compareAtPrice }
  } = variant;

  const [open, setOpen] = useState({});
  const [show, setShow] = useState(false);
  const target = useRef(null);

  const round = (avg) => (avg ? Math.round(avg) : 0);
  const prec = (avg) => (isNaN(avg) ? "0.0" : Number.parseFloat(avg).toPrecision(2));
  const scrollToReviews = () => document.getElementById("y-product-reviews")?.scrollIntoView({
    behavior: "smooth", block: "start"
  });

  return (
    <div className={pdpService}>
      <div className="container-wide">
        <div className={serviceContent}>
          <figure className={scFigure}>
            <img className={scImg} src={getImagePath(variant.variantImages && variant.variantImages[0])} alt="[Missing Image]" />
          </figure>
          <div className={serviceContentText}>
            <h1 className={sTitle}>{product.title}</h1>

            {totalReviews > 0 && (
              <a className={yStars} onClick={scrollToReviews}>
                {Array(round(averageRating)).fill().map((_, index) => (
                  <div key={index} className={clipStar}>
                    <StarFill />
                  </div>
                ))}
                <span className={ratingText}>
                  {prec(averageRating)} ({totalReviews} {locale.pages.reviews.reviews})
                </span>
              </a>
            )}

            <p className={sPrice}>
              <Money value={price} currency={price.currencyCode} />
              {compareAtPrice.currencyCode !== "NONE" && <span><Money value={compareAtPrice} currency={compareAtPrice.currencyCode} /></span>}
            </p>
            <PDPCampaign content={product?.content} pdpSettings={settings?.PDPSettings} />
            <div className={sDescription}>
              <RichText blocks={miniDescription} />
            </div>
            <Accordion className="pointer">
              {productTabs.map((_tab, index) => (
                <Card key={index} className={accordionCard}>
                  <Accordion.Toggle
                    as={Card.Header}
                    eventKey={_tab._key}
                    className={cardHeader}
                    onClick={() => {
                      setOpen(open === _tab._key ? null : _tab._key);
                      trackDropdownClick(open === _tab._key ? "close" : "open", _tab.title);
                    }}
                  >
                    <p className={accordionTitle}>{_tab.title}</p>
                    <p className={accordionSubtitle}>{_tab.subtitle}</p>
                    <span className={`${accordionIcon} ${_tab._key !== open ? "" : accordionIconOpen}`}>
                      <ChevronDown />
                    </span>
                  </Accordion.Toggle>
                  <Accordion.Collapse eventKey={_tab._key}>
                    <Card.Body className={accordionBody}>
                      <RichText blocks={_tab.content} />
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>
              ))}
            </Accordion>
            {variant?.crossSellProducts?.variants && (
              <CrossSellProducts
                title={variant?.crossSellProducts?.title}
                variants={variant?.crossSellProducts?.variants}
                crossSellVariants={crossSellVariants}
                setCrossSellVariants={setCrossSellVariants}
                totalPrice={totalPrice}
                setTotalPrice={setTotalPrice}
                totalCompareAtPrice={totalCompareAtPrice > 0 ? totalCompareAtPrice : totalPrice}
                setTotalCompareAtPrice={setTotalCompareAtPrice}
                template={pdpTemplate}
                ecoPackage={variant?.ecoPackage}
                pdpSettings={variant?.productSettings}
                overwriteSettingsCrossSell={variant?.enableCrossSell}
                crossSellStyle={variant?.crossSellProducts?.crossSellStyle}
                uniqueKey={variant?.crossSellProducts?._rev}
              />
            )}
            <button className={sBtn} onClick={handleAddToCart}>{locale.products.addToCart}</button>
            <div className={freeReturns}>
              <div className={frContent}>
                <img src={Truck} alt="Truck icon" />
                <p className={shippingEta}>
                  {miniIcons?.shippingETA && (
                    <span className={tooltipInfo} ref={target} onMouseEnter={() => setShow(true)} onMouseOut={() => setShow(false)}>
                      {miniIcons?.shippingETA}
                    </span>
                  )}
                </p>
              </div>
              {miniIcons?.toolTipMessage && (
                <Overlay target={target.current} show={show} placement="bottom">
                  {(props) => <Tooltip id="overlay-example" {...props}>{miniIcons?.toolTipMessage}</Tooltip>}
                </Overlay>
              )}
              {product?.content?.text && <p className={serviceDescription}>{product?.content?.text}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdpService;
